// AGC: LoginPage - Main login UI component
// AGC: Supports Google, Apple, Email/Password, Phone, and Biometric login
// AGC: Features video background with glassmorphic card overlay

import React, { useState } from 'react'
import { 
  Link2, 
  Mail, 
  Smartphone, 
  Eye, 
  EyeOff,
  Fingerprint,
  ScanFace,
  Loader2,
  X,
  ArrowLeft,
  Send,
} from 'lucide-react'
import { useAuth, useBiometric } from '../../hooks'

// AGC: Login method tabs
type LoginMethod = 'social' | 'email' | 'phone'

interface LoginPageProps {
  onNavigateToRegister: () => void
}

/**
 * AGC: LoginPage - Main authentication screen
 * AGC: Features video background and glassmorphic login card
 */
export const LoginPage: React.FC<LoginPageProps> = ({ onNavigateToRegister }) => {
  const {
    isLoading,
    error,
    clearError,
    loginWithGoogle,
    loginWithApple,
    loginWithEmail,
    loginWithPhone,
    verifyOTP,
    resetPassword,
  } = useAuth()

  const {
    isAvailable: isBiometricAvailable,
    isEnrolled: isBiometricEnrolled,
    authenticateWithBiometric,
    biometricType,
  } = useBiometric()

  // AGC: Local state
  const [activeMethod, setActiveMethod] = useState<LoginMethod>('social')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [otp, setOtp] = useState('')
  const [showOtpInput, setShowOtpInput] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showResetPassword, setShowResetPassword] = useState(false)
  const [resetEmailSent, setResetEmailSent] = useState(false)

  // AGC: Handle email login
  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) return
    await loginWithEmail({ email, password })
  }

  // AGC: Handle phone login - send OTP
  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!phoneNumber) return
    
    // AGC: Format phone number for Brazil
    let formattedPhone = phoneNumber.replace(/\D/g, '')
    if (!formattedPhone.startsWith('+')) {
      formattedPhone = '+55' + formattedPhone
    }
    
    await loginWithPhone(formattedPhone, 'recaptcha-container')
    setShowOtpInput(true)
  }

  // AGC: Handle OTP verification
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!otp) return
    await verifyOTP(otp)
  }

  // AGC: Handle biometric login
  const handleBiometricLogin = async () => {
    const success = await authenticateWithBiometric()
    if (success) {
      // AGC: User is already authenticated via Firebase persistence
      console.log('✅ AGC: Biometric authentication successful')
    }
  }

  // AGC: Handle password reset
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    await resetPassword(email)
    setResetEmailSent(true)
  }

  // AGC: Get biometric button content with Lucide icons
  const getBiometricButtonContent = () => {
    if (biometricType === 'face') return { icon: <ScanFace className="w-5 h-5" />, text: 'Entrar com Face ID' }
    if (biometricType === 'iris') return { icon: <ScanFace className="w-5 h-5" />, text: 'Entrar com Íris' }
    return { icon: <Fingerprint className="w-5 h-5" />, text: 'Entrar com Impressão Digital' }
  }

  // AGC: Common input styles for high contrast
  const inputClassName = "w-full px-4 py-3 bg-white text-gray-900 border border-gray-300 rounded-xl placeholder:text-gray-400 focus:ring-2 focus:ring-[var(--accent-primary)] focus:border-transparent"

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* AGC: Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src="/videos/bg.mp4" type="video/mp4" />
      </video>
      
      {/* AGC: Dark overlay for text readability */}
      <div className="absolute inset-0 bg-black/50" />
      
      {/* AGC: Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md">
          {/* AGC: Logo and subtitle - 50% size */}
          <div className="text-center mb-8">
            <img 
              src="/logologin.png" 
              alt="DroidFarm" 
              className="h-10 w-auto mx-auto drop-shadow-lg"
            />
            <p className="text-white/80 mt-2 drop-shadow">Gestão agrícola inteligente</p>
          </div>

          {/* AGC: Login Card - Enhanced glassmorphism style */}
          <div className="bg-white/60 backdrop-blur-2xl rounded-2xl shadow-2xl p-6 border border-white/20">
            {/* AGC: Error message */}
            {error && (
              <div className="bg-red-50/80 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
                <span className="text-sm">{error}</span>
                <button onClick={clearError} className="text-red-500 hover:text-red-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* AGC: Biometric login button (if available and enrolled) */}
            {isBiometricAvailable && isBiometricEnrolled && !showResetPassword && (
              <button
                onClick={handleBiometricLogin}
                disabled={isLoading}
                className="w-full py-3 px-4 bg-slate-800 text-white rounded-xl font-medium mb-4 flex items-center justify-center gap-2 hover:bg-slate-700 active:ring-4 active:ring-[var(--ring-click-primary)] transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {getBiometricButtonContent().icon}
                    <span>{getBiometricButtonContent().text}</span>
                  </>
                )}
              </button>
            )}

            {/* AGC: Password Reset Form */}
            {showResetPassword ? (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-800 text-center mb-4">
                  Recuperar Senha
                </h2>
                
                {resetEmailSent ? (
                  <div className="text-center py-4">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Send className="w-8 h-8 text-emerald-600" />
                    </div>
                    <p className="text-gray-600">
                      Email enviado! Verifique sua caixa de entrada para redefinir sua senha.
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="seu@email.com"
                        className={inputClassName}
                        required
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3 px-4 text-white rounded-xl font-medium hover:opacity-90 active:ring-4 active:ring-[var(--ring-click-primary)] transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                      style={{ backgroundColor: 'var(--accent-primary)' }}
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>Enviar Link de Recuperação</span>
                        </>
                      )}
                    </button>
                  </>
                )}
                
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPassword(false)
                    setResetEmailSent(false)
                  }}
                  className="w-full text-gray-600 hover:text-gray-800 flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Voltar ao login</span>
                </button>
              </form>
            ) : (
              <>
                {/* AGC: Method tabs - Minimalist Lucide icons */}
                <div className="flex border-b border-gray-200/60 mb-6">
                  <button
                    onClick={() => setActiveMethod('social')}
                    className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
                      activeMethod === 'social'
                        ? 'text-[var(--accent-dark-text)]'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    style={activeMethod === 'social' ? { borderColor: 'var(--accent-primary)' } : {}}
                  >
                    <Link2 className="w-4 h-4" />
                    <span>Social</span>
                  </button>
                  <button
                    onClick={() => setActiveMethod('email')}
                    className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
                      activeMethod === 'email'
                        ? 'text-[var(--accent-dark-text)]'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    style={activeMethod === 'email' ? { borderColor: 'var(--accent-primary)' } : {}}
                  >
                    <Mail className="w-4 h-4" />
                    <span>Email</span>
                  </button>
                  <button
                    onClick={() => setActiveMethod('phone')}
                    className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
                      activeMethod === 'phone'
                        ? 'text-[var(--accent-dark-text)]'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    style={activeMethod === 'phone' ? { borderColor: 'var(--accent-primary)' } : {}}
                  >
                    <Smartphone className="w-4 h-4" />
                    <span>Telefone</span>
                  </button>
                </div>

                {/* AGC: Social login - minimal monochrome style */}
                {activeMethod === 'social' && (
                  <div className="space-y-3">
                    <button
                      onClick={loginWithGoogle}
                      disabled={isLoading}
                      className="w-full py-3 px-4 bg-slate-100/80 border border-slate-200/60 rounded-xl font-medium text-slate-700 hover:bg-slate-200/80 active:ring-4 active:ring-[var(--ring-click-primary)] transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin text-slate-600" />
                      ) : (
                        /* AGC: Monochrome Google icon */
                        <svg className="w-5 h-5 text-slate-600" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      )}
                      <span>{isLoading ? 'Conectando...' : 'Continuar com Google'}</span>
                    </button>

                    <button
                      onClick={loginWithApple}
                      disabled={isLoading}
                      className="w-full py-3 px-4 bg-slate-800/90 text-white rounded-xl font-medium hover:bg-slate-700 active:ring-4 active:ring-[var(--ring-click-primary)] transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        /* AGC: Apple icon */
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                        </svg>
                      )}
                      <span>{isLoading ? 'Conectando...' : 'Continuar com Apple'}</span>
                    </button>
                  </div>
                )}

                {/* AGC: Email login */}
                {activeMethod === 'email' && (
                  <form onSubmit={handleEmailLogin} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="seu@email.com"
                        className={inputClassName}
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Senha
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          className={`${inputClassName} pr-12`}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowResetPassword(true)}
                      className="text-sm hover:opacity-80"
                      style={{ color: 'var(--accent-dark-text)' }}
                    >
                      Esqueceu a senha?
                    </button>
                    
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3 px-4 text-white rounded-xl font-medium hover:opacity-90 active:ring-4 active:ring-[var(--ring-click-primary)] transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                      style={{ backgroundColor: 'var(--accent-primary)' }}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Entrando...</span>
                        </>
                      ) : (
                        'Entrar'
                      )}
                    </button>
                  </form>
                )}

                {/* AGC: Phone login */}
                {activeMethod === 'phone' && (
                  <div className="space-y-4">
                    {!showOtpInput ? (
                      <form onSubmit={handleSendOTP} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Número de Telefone
                          </label>
                          <div className="flex gap-2">
                            <div className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-xl text-gray-600">
                              +55
                            </div>
                            <input
                              type="tel"
                              value={phoneNumber}
                              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                              placeholder="41 99999-9999"
                              className={`flex-1 ${inputClassName}`}
                              required
                            />
                          </div>
                        </div>
                        
                        <button
                          type="submit"
                          disabled={isLoading}
                          className="w-full py-3 px-4 text-white rounded-xl font-medium hover:opacity-90 active:ring-4 active:ring-[var(--ring-click-primary)] transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                          style={{ backgroundColor: 'var(--accent-primary)' }}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              <span>Enviando...</span>
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4" />
                              <span>Enviar Código SMS</span>
                            </>
                          )}
                        </button>
                      </form>
                    ) : (
                      <form onSubmit={handleVerifyOTP} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Código de Verificação
                          </label>
                          <input
                            type="text"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            className={`${inputClassName} text-center text-2xl tracking-widest`}
                            maxLength={6}
                            required
                          />
                          <p className="text-sm text-gray-500 mt-2 text-center">
                            Digite o código enviado para +55 {phoneNumber}
                          </p>
                        </div>
                        
                        <button
                          type="submit"
                          disabled={isLoading || otp.length !== 6}
                          className="w-full py-3 px-4 text-white rounded-xl font-medium hover:opacity-90 active:ring-4 active:ring-[var(--ring-click-primary)] transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                          style={{ backgroundColor: 'var(--accent-primary)' }}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              <span>Verificando...</span>
                            </>
                          ) : (
                            'Verificar Código'
                          )}
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => {
                            setShowOtpInput(false)
                            setOtp('')
                          }}
                          className="w-full text-gray-600 hover:text-gray-800 flex items-center justify-center gap-1"
                        >
                          <ArrowLeft className="w-4 h-4" />
                          <span>Alterar número</span>
                        </button>
                      </form>
                    )}
                  </div>
                )}

                {/* AGC: Register link */}
                <div className="mt-6 text-center">
                  <p className="text-gray-600">
                    Não tem uma conta?{' '}
                    <button
                      onClick={onNavigateToRegister}
                      className="font-medium hover:opacity-80"
                      style={{ color: 'var(--accent-dark-text)' }}
                    >
                      Criar conta
                    </button>
                  </p>
                </div>
              </>
            )}
          </div>

          {/* AGC: reCAPTCHA container (invisible) */}
          <div id="recaptcha-container"></div>

          {/* AGC: Footer */}
          <p className="text-center text-white/70 text-sm mt-6 drop-shadow">
            © 2025 DroidFarm - Gestão Agrícola
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
